import type { FC } from "react";
import { useMemo, useState } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useUnitSelector, type Unit } from "src/api/sure-api/units/useUnitSelector";

interface UnitAutocompleteProps {
    value: Unit | null;
    onChange: (option: Unit | null) => void;
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    sx?: object;
}

export const UnitAutocomplete: FC<UnitAutocompleteProps> = ({
    value,
    onChange,
    placeholder,
    disabled = false,
    error = false,
    helperText,
    sx = {},
}) => {
    const { t } = useTranslation();
    const { delayedSearch, setSearch } = useDelayedSearch();
    const [inputValue, setInputValue] = useState("");

    // Use server-side search with debounced search term
    const { units = [], isLoading } = useUnitSelector({
        searchText: delayedSearch,
        enabled: true,
        itemsPerPage: 50,
    });

    // Ensure current value is always available
    const filteredUnits = useMemo(() => {
        let filtered = units;

        // Always include current value if it exists and isn't already in the list
        if (value && !filtered.some((unit) => unit.id === value.id)) {
            filtered = [value, ...filtered];
        }

        return filtered;
    }, [units, value]);

    return (
        <Autocomplete
            options={filteredUnits}
            value={value}
            inputValue={inputValue}
            onChange={(_, newValue) => {
                onChange(newValue);
                setInputValue(""); // Clear input after selection
            }}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
                setSearch(newInputValue);
            }}
            getOptionLabel={(option) => option.title || ""}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            loading={isLoading}
            disabled={disabled}
            filterOptions={(options) => options}
            noOptionsText={
                isLoading ? t("common.loading", "Loading...")
                : delayedSearch ?
                    t("common.noResults", "No results")
                :   t("common.typeToSearch", "Type to search...")
            }
            loadingText={t("common.loading", "Loading...")}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={placeholder || t("unit.select.placeholder", "Select a unit")}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoading && delayedSearch ?
                                    <CircularProgress color="inherit" size={20} />
                                :   null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "24px",
                            backgroundColor: "#FFFFFF",
                            height: "40px",
                            "& fieldset": {
                                borderColor: "#E7E6E4",
                            },
                            "&:hover fieldset": {
                                borderColor: "#D0D0D0",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                            },
                        },
                        "& .MuiOutlinedInput-input": {
                            fontSize: "0.875rem",
                            padding: "0px 16px !important",
                            color: "#393A36",
                            "&::placeholder": {
                                color: "#A3A2A0",
                                opacity: 1,
                            },
                        },
                        ...sx,
                    }}
                />
            )}
        />
    );
};
